import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../../_shared/types/channel.type';

export type TGetListIntegrationWhatsappResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationWhatsappParams = FetchParams;

export const getListIntegrationWhatsapp = async (
  params: TGetListIntegrationWhatsappParams
): Promise<TGetListIntegrationWhatsappResponse> => {
  const url = '/integration/whatsapp/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY = 'get-list-integration-whatsapp';

export const getListIntegrationWhatsappQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationWhatsappParams,
  select?: (data: TGetListIntegrationWhatsappResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationWhatsapp(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationWhatsappQuery<TData>(options: {
  params: TGetListIntegrationWhatsappParams;
  queryConfig?: QueryConfig<typeof getListIntegrationWhatsappQueryOptions<TData>>;
  select: (data: TGetListIntegrationWhatsappResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationWhatsappQuery(options: {
  params: TGetListIntegrationWhatsappParams;
  queryConfig?: QueryConfig<typeof getListIntegrationWhatsappQueryOptions<TGetListIntegrationWhatsappResponse>>;
}): ReturnType<typeof useQuery<TGetListIntegrationWhatsappResponse>>;

// Implementation
export function useGetListIntegrationWhatsappQuery<TData = TGetListIntegrationWhatsappResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationWhatsappParams;
  queryConfig?: QueryConfig<typeof getListIntegrationWhatsappQueryOptions<TData | TGetListIntegrationWhatsappResponse>>;
  select?: (data: TGetListIntegrationWhatsappResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationWhatsappQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListIntegrationWhatsappResponse ? TGetListIntegrationWhatsappResponse : TData>
  >;
}
